import { Address } from 'react-daum-postcode';
import { Roles } from '@consts/role';
import { PartnerProfile } from './partner';
import { AddressInfo } from './product';

export enum LoginType {
  EMAIL = 'email',
  KAKAO = 'kakao',
  NAVER = 'naver',
  APPLE = 'apple',
}
export enum TutorContractType {
  FREELANCER = 'freelancer',
  BUSINESS = 'business',
}

export enum DayOfWeek {
  SUNDAY = 1,
  MONDAY = 2,
  TUESDAY = 3,
  WEDNESDAY = 4,
  THURSDAY = 5,
  FRIDAY = 6,
  SATURDAY = 7,
}

export interface NewAddress extends Address {
  postcode: string;
  postcode1: string;
  postcode2: string;
  postcodeSeq: string;
  address2: string;
}

export interface ChatSetting {
  chatEnabled: boolean;
  timeSettingMode?: 'same' | 'individual';
  chatSchedule: {
    dayOfWeek: DayOfWeek;
    chatStartTime: string;
    chatEndTime: string;
  }[];
  isHolidayChatEnabled?: boolean;
  holidaySchedule?: {
    startTime: string;
    endTime: string;
  };
  unavailableMessage: string;
}

export interface UserProfile {
  id?: string;
  name?: string;
  nickname?: string;
  educationArea?: any;
  agreement?: boolean;
  homepageId?: string;
  educationTitle?: string;
  imageUrl?: string;
  phone?: string;
  positiveWeeks?: string[];
  favorites?: string[];
  contract?: Partial<TutorContract>;
  creditPoints?: number;
  coupons?: string[];
  otherAlarmAgree?: boolean;
  marketingEmailAgree?: boolean;
  marketingSMSAgree?: boolean;
  children?: ChildInfo[];
  address?: NewAddress;
  categorySubList?: string[];
  customerServiceUrl?: string;
  customerServicePhone?: string;
  mainCustomerService?: string;
  salesChannels?: string[];
  message?: string;
  chatSetting?: ChatSetting;
  contentWrite?: {
    denied: boolean;
    deniedReason?: string;
    deniedUntil?: Date;
  };
}

export interface UserMembership {
  joinDate: Date;
  qualified?: boolean;
  qualifiedAt?: Date;
}

export interface User {
  id: string;
  _id: string;
  password?: string;
  email: string;
  loginType: LoginType;
  role: Roles;
  roles?: Roles[];
  profile?: UserProfile;
  partnerProfile?: PartnerProfile;
  deletedAt?: Date;
  membership?: UserMembership;
  partners?: { id: string; nickname: string }[];
}

export interface LoginParam {
  email: string;
  password: string;
  skipToast?: boolean;
  checkEmail?: boolean;
}

export type ChildInfoKeyType = 'name' | 'birthday' | 'gender';
export class ChildInfo {
  name?: string;
  birthday?: string;
  gender?: string;

  constructor(data: { name?: string; birthday?: string; gender?: string }) {
    this.name = data.name;
    this.birthday = data.birthday;
    this.gender = data.gender;
  }

  toString() {
    return `${this.name}-${this.birthday}-${this.gender}`;
  }
}

export interface TutorContract {
  contractType: TutorContractType;
  contractPhoto: string;
  licensePhoto: string;
  bankName: string;
  bankAccountHolder: string;
  bankAccountNumber: string;
  bankAccountPhoto: string;
  contractEmail: string;
  businessName: string;
  representative: string;
  address: NewAddress;
  businessNumber: string;
}

export interface UpdatePasswordParam {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  userId?: string;
}

export interface UpdateMeParam {
  password?: UpdatePasswordParam;
  name?: string;
  phoneNumber?: string;
  address?: Address;
  categorySubList?: string[];
  contract?: Partial<TutorContract>;
  children?: ChildInfo[];
  mainCustomerService?: string;
  customerServicePhone?: string;
  customerServiceUrl?: string;
  userId?: string;
  chatSetting?: ChatSetting;
}

export interface AppToken {
  token: string;
  os: string;
  appVersion?: string;
  iosOriginToken?: string;
}

export interface JoinBody {
  userId?: string;
  role: Roles;
  type: 'email' | 'naver' | 'kakao' | 'apple';
  email: string;
  naverId?: string;
  kakaoId?: string;
  appleId?: string;
  password?: string;
  profileImage: string;
  profileImageTemp?: string;
  name: string;
  phoneNumber: string;
  address: AddressInfo;
  contractType: TutorContractType;
  token: AppToken;
  nickname: string;
  intro: string;
  message?: string;
  bankName: string;
  bankAccountHolder: string;
  bankAccountNumber: string;
  businessName?: string;
  businessNumber?: string;
  representative?: string;
  contractEmail?: string;
  licensePhoto: string;
  bankAccountPhoto: string;
}

export interface KakaoToken {
  token_type: string;
  access_token: string;
  id_token?: string;
  expires_in: number;
  refresh_token: string;
  refresh_token_expires_in: number;
  scope?: string;
}

export interface KakaoUser {
  id: number;
  connected_at: string;
  properties: Properties;
  kakao_account: KakaoAccount;
}

export interface Properties {
  nickname: string;
  profile_image: string;
  thumbnail_image: string;
}

export interface KakaoAccount {
  profile_needs_agreement: boolean;
  profile: Profile;
  has_email: boolean;
  email_needs_agreement: boolean;
  is_email_valid: boolean;
  is_email_verified: boolean;
  email: string;
  name: string;
}

export interface Profile {
  nickname: string;
  thumbnail_image_url: string;
  profile_image_url: string;
  is_default_image: boolean;
  is_default_nickname: boolean;
}

export interface NaverUser {
  id: string;
  nickname: string;
  profile_image: string;
  email: string;
  name: string;
}

export interface AppleUser {
  id: string;
  email?: string;
}
